import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Class, ClassMember, User } from '../types/alarm';
import { userDataService } from '../services/userDataService';
import { defaultSetService } from '../services/defaultSetService';
import { memberProfilePhotoService } from '../services/memberProfilePhotoService';
import CachedProfileImage from './CachedProfileImage';
import MemberProfileImageUploadModal from './MemberProfileImageUploadModal';
import {
  MemberProfileDisplayMode,
  readMemberProfileDisplayMode,
  writeMemberProfileDisplayMode,
  resolveMemberProfileSource
} from '../utils/memberProfileDisplay';

interface MemberProfileSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  currentClass: Class;
  currentMemberGroup: string;
}

const MemberProfileSettingsModal: React.FC<MemberProfileSettingsModalProps> = ({
  isOpen,
  onClose,
  user,
  currentClass,
  currentMemberGroup
}) => {
  const [members, setMembers] = useState<ClassMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [displayMode, setDisplayMode] = useState<MemberProfileDisplayMode>('svg');
  const [selectedMember, setSelectedMember] = useState<ClassMember | null>(null);
  const [error, setError] = useState('');

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const aNum = Number(a.number || '99999');
      const bNum = Number(b.number || '99999');
      return aNum - bNum;
    });
  }, [members]);

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        const [loadedMembers, mode] = await Promise.all([
          userDataService.loadClassMembers(user.id, currentClass.id, currentMemberGroup),
          defaultSetService.loadMemberProfileDisplayMode(user.id, currentClass.id).catch(() =>
            readMemberProfileDisplayMode(user.id, currentClass.id)
          )
        ]);
        setMembers(loadedMembers);
        setDisplayMode(mode);
        writeMemberProfileDisplayMode(user.id, currentClass.id, mode);
      } catch (e) {
        console.error(e);
        setError('멤버 정보를 불러오지 못했습니다.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [isOpen, user.id, currentClass.id, currentMemberGroup]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleDisplayModeChange = async (mode: MemberProfileDisplayMode) => {
    if (mode === displayMode) return;
    setDisplayMode(mode);
    writeMemberProfileDisplayMode(user.id, currentClass.id, mode);
    try {
      await defaultSetService.updateMemberProfileDisplayMode(user.id, currentClass.id, mode);
    } catch (e) {
      console.error(e);
    }
  };

  const handleUploadSave = async (blob: Blob) => {
    if (!selectedMember) return;
    const memberId = selectedMember.id;
    const photoProfileVersion = Date.now();
    const uploaded = await memberProfilePhotoService.uploadMemberPhoto(user.id, currentClass.id, memberId, blob);
    await userDataService.updateMemberPhotoProfile(
      user.id,
      currentClass.id,
      memberId,
      uploaded.path,
      uploaded.url,
      photoProfileVersion,
      currentMemberGroup
    );
    setMembers(prev =>
      prev.map(m =>
        m.id === memberId
          ? {
              ...m,
              photoProfilePath: uploaded.path,
              photoProfileUrl: uploaded.url,
              photoProfileVersion
            }
          : m
      )
    );
  };

  const handleDeletePhoto = async (member: ClassMember) => {
    try {
      await memberProfilePhotoService.deleteMemberPhoto(user.id, currentClass.id, member.id);
      await userDataService.updateMemberPhotoProfile(
        user.id,
        currentClass.id,
        member.id,
        undefined,
        undefined,
        undefined,
        currentMemberGroup
      );
      setMembers(prev =>
        prev.map(m =>
          m.id === member.id
            ? { ...m, photoProfilePath: undefined, photoProfileUrl: undefined, photoProfileVersion: undefined }
            : m
        )
      );
    } catch (e) {
      console.error(e);
      alert('프로필 사진 삭제에 실패했습니다.');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Overlay onClick={onClose} />
      <Container onClick={(e) => e.stopPropagation()}>
        <Header>
          <HeaderSideSpacer aria-hidden />
          <HeaderCenter>
            <ModalTitle>멤버 사진 프로필 설정</ModalTitle>
            <ToggleSwitch
              $isOn={displayMode === 'photo'}
              role="switch"
              aria-checked={displayMode === 'photo'}
              aria-label="프로필 이미지를 사진 프로필로 표시하기"
              tabIndex={0}
              onClick={() => handleDisplayModeChange(displayMode === 'photo' ? 'svg' : 'photo')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleDisplayModeChange(displayMode === 'photo' ? 'svg' : 'photo');
                }
              }}
            >
              <ToggleSlider $isOn={displayMode !== 'photo'}>OFF</ToggleSlider>
              <ToggleSlider $isOn={displayMode === 'photo'}>ON</ToggleSlider>
            </ToggleSwitch>
          </HeaderCenter>
          <CloseButton type="button" onClick={onClose} aria-label="닫기">
            ×
          </CloseButton>
        </Header>

        <Body>
        {error && <ErrorText>{error}</ErrorText>}
        {isLoading ? (
          <LoadingText>불러오는 중...</LoadingText>
        ) : (
          <Grid>
            {sortedMembers.map(member => (
              <Card
                key={member.id}
                role="button"
                tabIndex={0}
                aria-label={`${member.number || '-'}번 ${member.name || '-'} 사진 프로필 설정`}
                onClick={() => setSelectedMember(member)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedMember(member);
                  }
                }}
              >
                <ProfileArea>
                  {member.photoProfileUrl ? (
                    <CachedProfileImage
                      imageId={resolveMemberProfileSource(member, 'photo')}
                      alt={`${member.name} 프로필`}
                      fallbackImageId="basic-0"
                    />
                  ) : (
                    <EmptyPhotoPlaceholder>
                      <EmptyPhotoCircle>+</EmptyPhotoCircle>
                    </EmptyPhotoPlaceholder>
                  )}
                </ProfileArea>
                <InfoText>{member.number || '-'}번</InfoText>
                <InfoText>{member.name || '-'}</InfoText>
                {member.photoProfileUrl && (
                  <DeletePhotoButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePhoto(member);
                    }}
                  >
                    사진 삭제
                  </DeletePhotoButton>
                )}
              </Card>
            ))}
          </Grid>
        )}
        </Body>
      </Container>

      {selectedMember && (
        <MemberProfileImageUploadModal
          isOpen={!!selectedMember}
          memberName={selectedMember.name}
          onClose={() => setSelectedMember(null)}
          onSave={handleUploadSave}
        />
      )}
    </>
  );
};

export default MemberProfileSettingsModal;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30010;
  background: rgba(0, 0, 0, 0.5);
`;

const Container = styled.div`
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: min(1260px, calc(100vw - 24px));
  max-height: min(920px, calc(100vh - 24px));
  overflow-y: auto;
  z-index: 30011;
  border-radius: 12px;
  background: var(--color-surface);
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  display: flex;
  flex-direction: column;
  padding: 0;
`;

/* PointManagementModal(포인트 타입별 세트 설정)과 동일한 헤더·구분선 */
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
`;

const HeaderSideSpacer = styled.div`
  width: 32px;
  min-width: 32px;
  flex-shrink: 0;
`;

const HeaderCenter = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  min-width: 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border: none;
  background: transparent;
  color: var(--color-text-gray);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 20px;
  line-height: 1;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: var(--color-panel);
    color: var(--color-text-primary);
  }
`;

/* AssignmentSettingsModal(기본 환경설정)과 동일한 OFF/ON 토글 */
const ToggleSwitch = styled.div<{ $isOn: boolean }>`
  display: flex;
  background: var(--color-accent-strong);
  border-radius: 13px;
  padding: 2px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 26px;
  min-width: 120px;
  width: auto;
  flex-shrink: 0;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--color-surface), 0 0 0 4px var(--color-accent-strong);
  }

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => (props.$isOn ? '50%' : '2px')};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: var(--color-surface);
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }
`;

const ToggleSlider = styled.div<{ $isOn: boolean }>`
  flex: 1;
  padding: 2px 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${props =>
    props.$isOn ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)'};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  @media (max-width: 600px) {
    padding: 2px 10px;
    font-size: 10px;
  }

  @media (max-width: 480px) {
    padding: 2px 8px;
    font-size: 9px;
  }
`;

const Body = styled.div`
  padding: 24px;
  flex: 1;
  min-height: 0;
`;

/** 프로필 프레임(100px)보다 블럭이 약간 넓어지도록 좌우 여백 */
const MEMBER_CARD_WIDTH = 116;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, ${MEMBER_CARD_WIDTH}px);
  justify-content: center;
  gap: 14px;
`;

const Card = styled.div`
  width: ${MEMBER_CARD_WIDTH}px;
  min-width: ${MEMBER_CARD_WIDTH}px;
  max-width: ${MEMBER_CARD_WIDTH}px;
  box-sizing: border-box;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  background: var(--color-panel);
  padding: 6px 8px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: var(--color-accent-strong);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ProfileArea = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid #000;
  background: var(--color-surface);
`;

const EmptyPhotoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyPhotoCircle = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2px solid var(--color-text-muted);
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 600;
`;

const InfoText = styled.div`
  color: var(--color-text-primary);
  font-size: 13px;
  text-align: center;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeletePhotoButton = styled.button`
  margin-top: 2px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-muted);
  border-radius: 6px;
  font-size: 11px;
  padding: 2px 6px;
  cursor: pointer;
`;

const ErrorText = styled.div`
  color: var(--color-accent-strong);
  margin-bottom: 8px;
`;

const LoadingText = styled.div`
  color: var(--color-text-muted);
`;
